import type {
  ApontamentoReuniao,
  NovoApontamento,
  VisaoGeralMes,
  VisaoGeralPayload,
} from '@/types/dashboards';

/**
 * Regras da tela de apontamentos da reunião. Máquinas, SKUs e meses saem do retrato da
 * Visão geral: o apontamento fala a mesma língua do painel que ele explica.
 */

export interface GrupoDeMaquinas {
  rotulo: string;
  maquinas: string[];
}

export interface SkuOpcao {
  codigo: string;
  descricao: string;
}

export interface CatalogoApontamento {
  meses: VisaoGeralMes[];
  mesPadrao: string;
  grupos: GrupoDeMaquinas[];
  skus: SkuOpcao[];
}

const porNome = (a: string, b: string) => a.localeCompare(b, 'pt-BR');

function skusDoPayload(p: VisaoGeralPayload): SkuOpcao[] {
  const porCodigo = new Map<string, SkuOpcao>();
  for (const item of Object.values(p.itens)) {
    const codigo = item.codigo_datasul ?? item.codigo;
    if (!porCodigo.has(codigo)) porCodigo.set(codigo, { codigo, descricao: item.descricao });
  }
  return [...porCodigo.values()].sort((a, b) => porNome(a.descricao, b.descricao));
}

export function catalogoDaVisaoGeral(p: VisaoGeralPayload): CatalogoApontamento {
  const grupos = p.maquinas.grupos
    .map((g) => ({
      rotulo: g.rotulo,
      maquinas: [...new Set(p.maquinas.linhas.filter((l) => l.grupo === g.id).map((l) => l.maquina))].sort(porNome),
    }))
    .filter((g) => g.maquinas.length > 0);
  // O histórico (6 meses desde 27/10/2026, quando o padrão da Visão geral virou o mês atual).
  return { meses: p.meses_historico ?? p.meses, mesPadrao: p.mes_padrao, grupos, skus: skusDoPayload(p) };
}

/** Texto da opção do SKU no campo com sugestões. */
export const rotuloSku = (s: SkuOpcao): string => `${s.codigo} — ${s.descricao}`;

/**
 * O que foi digitado no campo de SKU: vazio = sem SKU; código ou "código — descrição" da
 * lista = o SKU; qualquer outra coisa é inválida (não entra SKU que o painel não conhece).
 */
export function skuDoTexto(texto: string, skus: SkuOpcao[]): SkuOpcao | null | 'invalido' {
  const limpo = texto.trim();
  if (!limpo) return null;
  return skus.find((s) => s.codigo === limpo || rotuloSku(s) === limpo) ?? 'invalido';
}

/** Mês de produção (16 a 15) em que a data cai; `undefined` fora da janela do painel. */
export function mesDaData(data: string, meses: VisaoGeralMes[]): VisaoGeralMes | undefined {
  return meses.find((m) => m.start <= data && data <= m.end);
}

export function formVazio(hoje: string): NovoApontamento {
  return {
    data: hoje,
    maquina: '',
    sku_codigo: '',
    sku_descricao: '',
    turno: '',
    motivo: '',
    quantidade: null,
    observacao: '',
  };
}

export function formDoApontamento(a: ApontamentoReuniao): NovoApontamento {
  const { data, maquina, sku_codigo, sku_descricao, turno, motivo, quantidade, observacao } = a;
  return { data, maquina, sku_codigo, sku_descricao, turno, motivo, quantidade, observacao };
}

/** Hoje no fuso de quem está na tela (a reunião é no Brasil), em AAAA-MM-DD. */
export function hojeIso(agora: Date = new Date()): string {
  const d = new Date(agora.getTime() - agora.getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 10);
}

export interface ResumoMotivo {
  motivo: string;
  rotulo: string;
  quantos: number;
}

/** Quantos apontamentos por motivo, do mais frequente para o menos. */
export function resumoPorMotivo(lista: ApontamentoReuniao[]): ResumoMotivo[] {
  const contagem = new Map<string, ResumoMotivo>();
  for (const a of lista) {
    const atual = contagem.get(a.motivo) ?? { motivo: a.motivo, rotulo: a.motivo_label, quantos: 0 };
    contagem.set(a.motivo, { ...atual, quantos: atual.quantos + 1 });
  }
  return [...contagem.values()].sort((a, b) => b.quantos - a.quantos || porNome(a.rotulo, b.rotulo));
}

export interface ErrosApontamento {
  geral: string;
  campos: Partial<Record<keyof NovoApontamento, string>>;
}

type CorpoErro = { error?: { message?: string; errors?: Record<string, string[] | string> } };

const primeira = (v: string[] | string): string => (Array.isArray(v) ? v[0] ?? '' : v);

/**
 * Erro do backend (`{"error": {"message", "errors": {campo: [msg]}}}`) em mensagem geral +
 * mensagem por campo. Erro de validação não tem `message` útil ("Erro no servidor").
 */
export function errosDaApi(erro: unknown, padrao: string): ErrosApontamento {
  const corpo = (erro as { response?: { data?: CorpoErro } })?.response?.data?.error;
  const errors = corpo?.errors ?? {};
  const campos: ErrosApontamento['campos'] = {};
  let semCampo = '';
  for (const [campo, msg] of Object.entries(errors)) {
    if (campo in formVazio('')) campos[campo as keyof NovoApontamento] = primeira(msg);
    else semCampo = semCampo || primeira(msg);
  }
  if (Object.keys(campos).length > 0) return { geral: semCampo || 'Confira os campos destacados.', campos };
  return { geral: semCampo || corpo?.message || padrao, campos };
}
